import React, { useEffect, useState, ChangeEvent } from 'react';
import LateralPanel from '../../../../muiComponents/LateralPanel';
import Title from '../../yourProperties/Title';
import Tag from '../../../../muiComponents/Tag';
import Tooltip from '../../../../muiComponents/Tooltip';
import SwitchInput from '../../../../muiComponents/switchInput/SwitchInput';
import Icon from '../../../../muiComponents/Icon';
import useTranslate from '../../../../customHooks/useTranslate';
import Grid from '../../../../muiComponents/Grid';
import GridChild from '../../../../muiComponents/GridChild';
import { SUPPLEMENT_DATE_TAG_COLOR } from '../../../../utils/supplements/supplementUtils';
import { adaptSupplementDetailFromDDBB } from '../../../../services/supplements/supplementAdapters';
import { useSearchParams } from '../../../../customHooks/useSearchParams';
import { useNotification } from '../../../../contexts/NotificationsContext';
import { FeedbackMessages } from '../../../../utils/commonVariables/messages';
import { categorizePeriod } from '../../../../utils/formatDates';
import {
    doubleLG_ColumnGrid,
    gridSpacing30,
} from '../../../../utils/commonVariables/grid';
import DateInput from '../../../../muiComponents/DateInput';
import LoadedOptionsTypeahead from '../../../../muiComponents/LoadedOptionsTypeahead';
import { useODataMultipleQueries } from '../../../../customHooks/useODataMultipleQueries';
import {
    readOnlyEntities,
    savableEntities,
} from '../../../../utils/commonVariables/entityKeys';
import Button from '../../../../muiComponents/Button';
import convertToOvariant from '../../../../utils/convertToOvariant';
import api from '../../../../services/api';
import endpoints from '../../../../utils/commonVariables/endpoints';
import { validateForm } from '../../../../utils/validateForms';
import LoadingTaxPanel from '../../taxes/LoadingTaxPanel';
import {
    useStopSalesComplementaryData,
    useStopSalesList,
} from '../../../contextsProperty/ContextStopSales';
import {
    getStopSaleData,
    saveStopSaleToDDBB,
} from '../../../../services/stopSales/stopSalesServices';
import {
    IStopSaleDetailState,
    IStopSalesComplementaryDataOption,
} from '../../../../types/stopSale/stopSalesTypes';
import {
    STOP_SALES_INITIAL_STATE,
    STOP_SALES_VALIDATION_INITIAL_STATE,
} from '../../../../utils/stopSales/stopSalesUtils';
import useUserProfile from '../../../../customHooks/useUserProfile';
import ReadOnlyUserAlert from '../../ReadOnlyUserAlert';

interface StopSalesPanelProps {
    open: boolean;
    onClose: () => void;
}

const StopSalesPanel = ({ open, onClose }: StopSalesPanelProps) => {
    const translate = useTranslate();
    const { setErrorMessage, setSuccessMessage } = useNotification();
    const { getSearchParam, removeSearchParam } = useSearchParams({
        base64: true,
    });
    const { readOnlyUser } = useUserProfile();

    const { supplierHotels } = useStopSalesComplementaryData();
    const { handleSearch } = useStopSalesList();

    const [detail, setDetail] = useState<IStopSaleDetailState>(
        STOP_SALES_INITIAL_STATE
    );
    const [isLoading, setIsLoading] = useState(false);
    const [formErrors, setFormErrors] = useState(
        STOP_SALES_VALIDATION_INITIAL_STATE
    );

    const idUrl = getSearchParam('id');

    const getDetail = async (id: string) => {
        try {
            setIsLoading(true);
            const { data } = await getStopSaleData(id);
            if (data.Result && data.StatusCode === 200) {
                setDetail(adaptSupplementDetailFromDDBB(data.Result));
            } else {
                removeSearchParam('id');
                setErrorMessage(FeedbackMessages.COMMON_ERROR);
            }
        } catch (error) {
            removeSearchParam('id');
            setErrorMessage(FeedbackMessages.COMMON_ERROR);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (idUrl) {
            if (idUrl !== 'new') {
                getDetail(idUrl);
            }
        }
    }, [idUrl]);

    const handleDetailChange = (
        updatedFields: Partial<IStopSaleDetailState>
    ) => {
        setDetail(prev => {
            return prev ? { ...prev, ...updatedFields } : prev;
        });
    };

    const isNew = !detail?.Id || parseInt(detail.Id) <= 0;
    const tagText = categorizePeriod(detail?.FromDate, detail?.ToDate);

    const validateFields = {
        FromDate: {
            value: detail?.FromDate ? new Date(detail?.FromDate) : null,
            inputKey: 'FromDate',
            validators: ['requiredDate'],
        },
        ToDate: {
            value: detail?.ToDate ? new Date(detail?.ToDate) : null,
            inputKey: 'ToDate',
            validators: ['requiredDate'],
        },
        FromToDate: {
            value: [detail?.FromDate, detail?.ToDate],
            inputKey: 'ToDate',
            validators: ['fromToDate'],
        },
        IdSupplierHotel: {
            value: detail?.IdSupplierHotel,
            validators: ['requiredIdEntity'],
        },
    };

    const handleValidateFields = () => {
        setFormErrors(prev => ({
            ...STOP_SALES_VALIDATION_INITIAL_STATE,
            isValidated: prev.isValidated,
        }));
        if (!formErrors.isValidated) {
            setFormErrors(prev => ({
                ...prev,
                isValidated: true,
            }));
        }
        const errors = validateForm(validateFields);
        if (errors.length) {
            for (const error of errors) {
                setFormErrors(prev => ({
                    ...prev,
                    [error.inputKey]: error.errorMessage,
                }));
            }
        }
        return errors.length === 0;
    };

    const handleDelete = async () => {
        if (isNew) {
            onClose();
            handleResetPopUp();
            return;
        }
        if (detail) {
            try {
                setIsLoading(true);
                const payload = convertToOvariant(
                    savableEntities.SupplierHotelStopSale,
                    {
                        Ids: [detail.Id],
                    }
                );
                const endpoint = endpoints.commonDeleteItems;
                const res = await api.post(endpoint, payload);

                if (res.data && res.status === 200) {
                    setSuccessMessage(FeedbackMessages.COMMON_SUCCESS_DELETE);
                    handleSearch();
                    onClose();
                    handleResetPopUp();
                } else {
                    setErrorMessage(FeedbackMessages.COMMON_ERROR);
                }
            } catch (error) {
                setErrorMessage(FeedbackMessages.COMMON_ERROR);
            } finally {
                setIsLoading(false);
            }
        }
    };

    const saveDDBB = async () => {
        const validated = handleValidateFields();
        if (!validated) return;
        try {
            setIsLoading(true);
            const { data } = await saveStopSaleToDDBB(detail);
            if (data) {
                setSuccessMessage(FeedbackMessages.COMMON_SUCCESS_UPDATE);
                handleSearch();
                onClose();
                handleResetPopUp();
            } else {
                setErrorMessage(FeedbackMessages.COMMON_ERROR);
            }
        } catch (error) {
            setErrorMessage(FeedbackMessages.COMMON_ERROR);
        } finally {
            setIsLoading(false);
        }
    };

    const handleResetPopUp = () => {
        setDetail(STOP_SALES_INITIAL_STATE);
        setFormErrors(STOP_SALES_VALIDATION_INITIAL_STATE);
    };

    const IdSupplierHotel = detail?.IdSupplierHotel || null;

    const supplierHotelDependentQueries = [
        {
            key: 'supplierHotelRoomTypes',
            endpoint: {
                entityName: readOnlyEntities.SupplierHotelRoomType,
                select: ['Id', 'Name', 'IdSupplierHotel'],
                orderBy: 'Name asc',
                filter: [{ IdSupplierHotel }],
            },
        },
        {
            key: 'supplierHotelMealPlans',
            endpoint: {
                entityName: readOnlyEntities.SupplierHotelMealPlan,
                select: ['Id', 'Name', 'IdSupplierHotel'],
                orderBy: 'Name asc',
                filter: [{ IdSupplierHotel }],
            },
        },
        {
            key: 'supplierHotelRatePlans',
            endpoint: {
                entityName: readOnlyEntities.SupplierHotelRatePlan,
                select: ['Id', 'Name', 'IdSupplierHotel'],
                orderBy: 'Name asc',
                filter: [{ IdSupplierHotel }],
            },
        },
    ];

    const {
        supplierHotelRoomTypes,
        supplierHotelMealPlans,
        supplierHotelRatePlans,
    } = useODataMultipleQueries({
        queries: supplierHotelDependentQueries,
        dependencies: [IdSupplierHotel],
        condition: !!IdSupplierHotel,
    });

    return (
        <LateralPanel open={open} className='padding-bottom-32'>
            <header className='flex flex-between flex-align-center '>
                <Title
                    text={translate(isNew ? 'new_stop_sale' : 'edit_stop_sale')}
                    className='text-secondary-color'
                />
                <div className='flex flex-align-center gap-20'>
                    {!detail?.FromDate && !detail?.ToDate && (
                        <Tag
                            text={tagText}
                            color={SUPPLEMENT_DATE_TAG_COLOR[tagText]}
                        />
                    )}

                    <Tooltip
                        title={translate(
                            detail?.Enabled ? 'enabled' : 'disabled'
                        )}
                    >
                        <div>
                            <SwitchInput
                                disabled={isLoading || readOnlyUser}
                                checked={detail?.Enabled || false}
                                labelText={''}
                                onChangeMethod={(
                                    _e: ChangeEvent<HTMLInputElement>,
                                    v: boolean
                                ) => {
                                    handleDetailChange({
                                        Enabled: v,
                                    });
                                }}
                            />
                        </div>
                    </Tooltip>
                    <Icon
                        iconName='Close'
                        className='cursor-pointer'
                        onClick={() =>
                            !isLoading && (onClose(), handleResetPopUp())
                        }
                    />
                </div>
            </header>
            <div className='padding-block-20'>
                <ReadOnlyUserAlert text='read_only_stop_sales' />
            </div>

            {isLoading ? (
                <main className='flex-column gap-20 height100perc padding-right-20 overflow-y-auto overflow-x-hidden padding-top-16'>
                    <LoadingTaxPanel />
                </main>
            ) : (
                <main className='flex-column gap-20 height100perc padding-right-20 overflow-y-auto overflow-x-hidden padding-top-16'>
                    <Grid
                        columnSpacing={gridSpacing30}
                        rowSpacing={gridSpacing30}
                        style={{ width: 'auto' }}
                    >
                        <GridChild gridProps={doubleLG_ColumnGrid}>
                            <LoadedOptionsTypeahead
                                required
                                disabled={
                                    supplierHotels.isLoading ||
                                    !isNew ||
                                    readOnlyUser
                                }
                                labelText='hotel'
                                options={supplierHotels.data || []}
                                idValue={detail?.IdSupplierHotel}
                                handleChange={(
                                    _event: React.SyntheticEvent,
                                    value: IStopSalesComplementaryDataOption | null
                                ) => {
                                    handleDetailChange({
                                        IdSupplierHotel: value
                                            ? parseInt(value.Id)
                                            : 0,
                                    });
                                }}
                                onBlur={() => {
                                    if (formErrors.isValidated) {
                                        handleValidateFields();
                                    }
                                }}
                                error={!!formErrors.IdSupplierHotel}
                                errorMessage={formErrors.IdSupplierHotel}
                            />
                        </GridChild>
                        <GridChild gridProps={doubleLG_ColumnGrid}>
                            <LoadedOptionsTypeahead
                                labelText='room'
                                options={supplierHotelRoomTypes.data || []}
                                idValue={detail?.IdSupplierHotelRoomType}
                                disabled={
                                    !detail?.IdSupplierHotel ||
                                    supplierHotelRoomTypes.isLoading ||
                                    readOnlyUser
                                }
                                handleChange={(
                                    _event: React.SyntheticEvent,
                                    value: IStopSalesComplementaryDataOption | null
                                ) => {
                                    handleDetailChange({
                                        IdSupplierHotelRoomType: value
                                            ? parseInt(value.Id)
                                            : 0,
                                    });
                                }}
                            />
                        </GridChild>
                        <GridChild gridProps={doubleLG_ColumnGrid}>
                            <LoadedOptionsTypeahead
                                labelText='rate_plan'
                                disabled={
                                    !detail?.IdSupplierHotel ||
                                    supplierHotelRatePlans.isLoading ||
                                    readOnlyUser
                                }
                                options={supplierHotelRatePlans.data || []}
                                idValue={detail?.IdSupplierHotelRatePlan}
                                handleChange={(
                                    _event: React.SyntheticEvent,
                                    value: IStopSalesComplementaryDataOption | null
                                ) => {
                                    handleDetailChange({
                                        IdSupplierHotelRatePlan: value
                                            ? parseInt(value.Id)
                                            : 0,
                                    });
                                }}
                            />
                        </GridChild>
                        <GridChild gridProps={doubleLG_ColumnGrid}>
                            <LoadedOptionsTypeahead
                                labelText='meal_plan'
                                disabled={
                                    !detail?.IdSupplierHotel ||
                                    supplierHotelMealPlans.isLoading ||
                                    readOnlyUser
                                }
                                options={supplierHotelMealPlans.data || []}
                                idValue={detail?.IdSupplierHotelMealPlan}
                                handleChange={(
                                    _event: React.SyntheticEvent,
                                    value: IStopSalesComplementaryDataOption | null
                                ) => {
                                    handleDetailChange({
                                        IdSupplierHotelMealPlan: value
                                            ? parseInt(value.Id)
                                            : 0,
                                    });
                                }}
                            />
                        </GridChild>
                        <GridChild gridProps={doubleLG_ColumnGrid}>
                            <DateInput
                                required
                                labelText='from'
                                value={detail?.FromDate}
                                onChange={(date: Date | null) => {
                                    handleDetailChange({
                                        FromDate: date,
                                    });
                                }}
                                onBlur={() => {
                                    if (formErrors.isValidated) {
                                        handleValidateFields();
                                    }
                                }}
                                error={
                                    !!formErrors.FromDate ||
                                    !!formErrors.FromToDate
                                }
                                errorMessage={
                                    formErrors.FromDate || formErrors.FromToDate
                                }
                                minDate={new Date()}
                                maxDate={detail?.ToDate || undefined}
                                disabled={readOnlyUser}
                            />
                        </GridChild>
                        <GridChild gridProps={doubleLG_ColumnGrid}>
                            <DateInput
                                required
                                labelText='to'
                                value={detail?.ToDate}
                                onChange={(date: Date | null) => {
                                    handleDetailChange({
                                        ToDate: date,
                                    });
                                }}
                                onBlur={() => {
                                    if (formErrors.isValidated) {
                                        handleValidateFields();
                                    }
                                }}
                                minDate={detail?.FromDate || new Date()}
                                error={
                                    !!formErrors.ToDate ||
                                    !!formErrors.FromToDate
                                }
                                errorMessage={
                                    formErrors.ToDate || formErrors.FromToDate
                                }
                                disabled={readOnlyUser}
                            />
                        </GridChild>
                    </Grid>
                </main>
            )}
            <footer className='flex flex-between margin-block-16'>
                <Button
                    variant='contained'
                    className={'delete-button'}
                    disabled={isLoading || readOnlyUser}
                    onClick={() => {
                        handleDelete();
                    }}
                >
                    {translate('delete')}
                </Button>
                <div className='flex gap-20'>
                    <Button
                        variant='outlined'
                        disabled={isLoading}
                        onClick={() => {
                            onClose();
                            handleResetPopUp();
                        }}
                    >
                        {translate('cancel')}
                    </Button>
                    <Button
                        variant='contained'
                        disabled={isLoading || readOnlyUser}
                        onClick={saveDDBB}
                    >
                        {translate('save')}
                    </Button>
                </div>
            </footer>
        </LateralPanel>
    );
};

export default StopSalesPanel;
